import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "wonyasoft-theme";
const SYSTEM_QUERY = "(prefers-color-scheme: dark)";
const MODES = ["system", "light", "dark"];
const DEFAULT_MODE = "system";
const FALLBACK_THEME = "light";
const THEME_COLORS = {
  light: "#c81d25",
  dark: "#1d2733",
};
const useBrowserLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const ThemeContext = createContext({
  mode: DEFAULT_MODE,
  resolvedTheme: FALLBACK_THEME,
  setMode: () => {},
  cycleMode: () => {},
});

function validMode(value) {
  return MODES.includes(value) ? value : DEFAULT_MODE;
}

function readStoredMode() {
  if (typeof window === "undefined") {
    return DEFAULT_MODE;
  }

  try {
    return validMode(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_MODE;
  }
}

function systemTheme() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return FALLBACK_THEME;
  }

  try {
    return window.matchMedia(SYSTEM_QUERY).matches ? "dark" : "light";
  } catch {
    return FALLBACK_THEME;
  }
}

function getSystemMediaQuery() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }

  try {
    return window.matchMedia(SYSTEM_QUERY);
  } catch {
    return null;
  }
}

function storeMode(mode) {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // The theme must remain usable when storage is blocked or unavailable.
  }
}

function applyTheme(mode, resolvedTheme) {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  root.dataset.themeMode = mode;
  root.dataset.theme = resolvedTheme;
  root.style.colorScheme = resolvedTheme;

  let themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement("meta");
    themeColorMeta.setAttribute("name", "theme-color");
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.setAttribute("content", THEME_COLORS[resolvedTheme]);
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemPreference, setSystemPreference] = useState(() =>
    typeof window === "undefined" ? FALLBACK_THEME : systemTheme(),
  );
  const resolvedTheme = mode === "system" ? systemPreference : mode;

  useEffect(() => {
    const mediaQuery = getSystemMediaQuery();
    if (!mediaQuery) {
      return undefined;
    }

    const handleChange = ({ matches }) => {
      setSystemPreference(matches ? "dark" : "light");
    };

    handleChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener?.(handleChange);
    return () => mediaQuery.removeListener?.(handleChange);
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY && event.key !== null) {
        return;
      }

      if (event.storageArea) {
        try {
          if (event.storageArea !== window.localStorage) {
            return;
          }
        } catch {
          return;
        }
      }

      setModeState(validMode(event.newValue));
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useBrowserLayoutEffect(() => {
    storeMode(mode);
    applyTheme(mode, resolvedTheme);
  }, [mode, resolvedTheme]);

  const setMode = useCallback((nextMode) => {
    setModeState((currentMode) => {
      const requestedMode =
        typeof nextMode === "function" ? nextMode(currentMode) : nextMode;
      return validMode(requestedMode);
    });
  }, []);

  const cycleMode = useCallback(() => {
    setModeState((currentMode) => {
      const currentIndex = MODES.indexOf(currentMode);
      return MODES[(currentIndex + 1) % MODES.length];
    });
  }, []);

  const value = useMemo(
    () => ({
      mode,
      resolvedTheme,
      setMode,
      cycleMode,
    }),
    [cycleMode, mode, resolvedTheme, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
